import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { cantieri } from '../../utils/api';
import SpeseTabPDF from './SpeseTabPDF';

const slugify = s => s.toLowerCase().replace(/[àáâãäå]/g,'a').replace(/[èéêë]/g,'e').replace(/[ìíîï]/g,'i').replace(/[òóôõö]/g,'o').replace(/[ùúûü]/g,'u').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }) : '';

const ICON_EDIT = <svg style={{ width: 14, height: 14 }} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const ICON_TRASH = <svg style={{ width: 14, height: 14 }} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>;
const ICON_CHECK = <svg style={{ width: 14, height: 14 }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>;
const ICON_X = <svg style={{ width: 14, height: 14 }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;

export default function SpeseTab({ cantiereId, pianoData, dashData }) {
  const [eventi, setEventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const navigate = useNavigate();

  const fetch = useCallback(() => {
    setLoading(true);
    cantieri.eventi(cantiereId)
      .then(res => {
        const data = res.data?.results || res.data || [];
        setEventi(data.filter(e => e.tipo === 'costo'));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cantiereId]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async (data) => {
    try {
      const imponibile = parseFloat(data.importo) || 0;
      const aliquota = parseFloat(data.aliquota_iva) || 0;
      const ivaCalc = +(imponibile * aliquota / 100).toFixed(2);
      await cantieri.addEvento(cantiereId, {
        tipo: 'costo', contenuto: data.descrizione,
        importo: imponibile, iva: ivaCalc, data_riferimento: data.data,
        metadata: { fonte: 'manuale', categoria: data.categoria || '', aliquota_iva: aliquota },
      });
      setShowForm(false); fetch();
    } catch {}
  };

  const handleDelete = async (eventoId) => {
    if (!window.confirm('Eliminare questa spesa?')) return;
    try { await cantieri.deleteEvento(cantiereId, eventoId); fetch(); } catch {}
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    const aliquotaSalvata = e.metadata?.aliquota_iva;
    const aliquotaDefault = (aliquotaSalvata != null) ? aliquotaSalvata
      : (e.importo > 0 && e.iva > 0 ? Math.round(Number(e.iva) / Number(e.importo) * 100) : 22);
    setEditForm({
      contenuto: e.contenuto || '',
      importo: e.importo || '',
      aliquota_iva: aliquotaDefault,
      data_riferimento: e.data_riferimento || '',
      categoria: e.metadata?.categoria || '',
    });
  };

  const handleSaveEdit = async (eventoId) => {
    try {
      const imponibile = parseFloat(editForm.importo) || 0;
      const aliquota = parseFloat(editForm.aliquota_iva) || 0;
      const ivaCalc = +(imponibile * aliquota / 100).toFixed(2);
      await cantieri.patchEvento(cantiereId, eventoId, {
        contenuto: editForm.contenuto,
        importo: imponibile,
        iva: ivaCalc,
        data_riferimento: editForm.data_riferimento,
        metadata: { categoria: editForm.categoria, aliquota_iva: aliquota },
      });
      setEditingId(null);
      fetch();
    } catch {}
  };

  if (loading) return <div style={{ padding: 20, color: 'var(--ds-muted)' }}>Caricamento spese...</div>;

  const totale = eventi.reduce((s, e) => s + Number(e.importo || 0), 0);
  const budgetTotale = pianoData?.budget_totale || 0;
  const residuo = budgetTotale - totale;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>Spese cantiere</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {eventi.length > 0 && (
            <PDFDownloadLink
              document={<SpeseTabPDF spese={eventi} cantiere={dashData?.cantiere} azienda={dashData?.azienda} budget={{ budget_totale: pianoData?.budget_totale || 0 }} />}
              fileName={'spese-' + slugify(dashData?.cantiere?.titolo || 'cantiere') + '-' + new Date().toISOString().slice(0, 10) + '.pdf'}
            >
              {({ loading }) => <button style={btnSmall} disabled={loading}>{loading ? '...' : '📄 PDF'}</button>}
            </PDFDownloadLink>
          )}
          <button onClick={() => setShowForm(true)} style={btnPrimary}>+ Aggiungi spesa</button>
        </div>
      </div>

      {/* KPI */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1,
        background: 'var(--ds-border)', borderRadius: 'var(--radius-base)', overflow: 'hidden', marginBottom: 20,
      }}>
        <KpiBox label="BUDGET SPESE" value={fmtEur(budgetTotale)} color="var(--ink)" />
        <KpiBox label="SPESO" value={fmtEur(totale)} color={budgetTotale > 0 ? (totale > budgetTotale * 0.8 ? 'var(--warn)' : 'var(--ok)') : 'var(--ink)'} />
        <KpiBox label="RESIDUO" value={fmtEur(residuo)} color={residuo >= 0 ? 'var(--brand)' : 'var(--ds-danger)'} />
      </div>

      {showForm && <CostoForm onSubmit={handleAdd} onCancel={() => setShowForm(false)} />}

      {eventi.length === 0 && !showForm ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ds-muted)', fontSize: 13 }}>Nessuna spesa registrata.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {eventi.map(e => {
            const cat = e.metadata?.categoria || '';
            const fornitore = e.metadata?.fornitore || '';
            const fonte = e.metadata?.fonte;
            const isEditing = editingId === e.id;

            if (isEditing) {
              return (
                <div key={e.id} style={{ padding: '12px 16px', borderRadius: 'var(--radius-base)', border: '2px solid var(--brand)', background: 'var(--chip)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 100px 120px', gap: 8, alignItems: 'end' }}>
                    <div>
                      <label style={labelStyle}>Descrizione</label>
                      <input value={editForm.contenuto} onChange={ev => setEditForm({ ...editForm, contenuto: ev.target.value })} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Imponibile</label>
                      <input type="number" step="0.01" value={editForm.importo} onChange={ev => setEditForm({ ...editForm, importo: ev.target.value })} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>IVA</label>
                      <select value={editForm.aliquota_iva} onChange={ev => setEditForm({ ...editForm, aliquota_iva: ev.target.value })} style={inputStyle}>
                        <option value={0}>Esente</option>
                        <option value={4}>4%</option>
                        <option value={10}>10%</option>
                        <option value={22}>22%</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Data</label>
                      <input type="date" value={editForm.data_riferimento} onChange={ev => setEditForm({ ...editForm, data_riferimento: ev.target.value })} style={inputStyle} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
                    <button onClick={() => setEditingId(null)} style={{ ...actionBtn, color: 'var(--ds-muted)' }}>{ICON_X} Annulla</button>
                    <button onClick={() => handleSaveEdit(e.id)} style={{ ...actionBtn, color: 'var(--brand)' }}>{ICON_CHECK} Salva</button>
                  </div>
                </div>
              );
            }

            return (
              <div key={e.id}
                onClick={() => fonte === 'ordine_fornitore' ? navigate('/fornitori') : null}
                style={{
                  padding: '14px 16px', borderRadius: 'var(--radius-base)',
                  border: '1px solid var(--ds-border)', background: 'var(--chip)',
                  display: 'flex', alignItems: 'center', gap: 14,
                  cursor: fonte === 'ordine_fornitore' ? 'pointer' : 'default',
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 'var(--radius-card)',
                  background: 'var(--ds-bg)', color: 'var(--zone-acqua)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, flexShrink: 0,
                }}>
                  {getCategoryIcon(cat, fonte)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ds-text)' }}>{e.contenuto || 'Costo'}</div>
                  <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>
                    {fornitore && (fornitore + ' · ')}
                    {fmtDate(e.data_riferimento || e.created_at)}
                    {cat && (' · ' + cat)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: 16, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>{fmtEur(e.importo)}</div>
                  {Number(e.iva) > 0 && (() => {
                    const aliq = e.metadata?.aliquota_iva != null
                      ? e.metadata.aliquota_iva
                      : Math.round(Number(e.iva) / Number(e.importo) * 100);
                    return <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 2 }}>+IVA {aliq}% ({fmtEur(e.iva)})</div>;
                  })()}
                </div>
                {/* Azioni matita + cestino */}
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={ev => { ev.stopPropagation(); startEdit(e); }} style={{ ...actionBtn, color: 'var(--ds-muted)' }} title="Modifica">{ICON_EDIT}</button>
                  <button onClick={ev => { ev.stopPropagation(); handleDelete(e.id); }} style={{ ...actionBtn, color: 'var(--ds-danger)' }} title="Elimina">{ICON_TRASH}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getCategoryIcon(cat, fonte) {
  if (fonte === 'ordine_fornitore') return '🏪';
  const map = {
    materiali: '🧱', attrezzatura: '🔧', smaltimento: '🚛',
    manodopera: '👷', trasporto: '🚚', noleggio: '📦',
  };
  return map[cat?.toLowerCase()] || '💸';
}

function KpiBox({ label, value, color }) {
  return (
    <div style={{ padding: '16px 20px', background: 'var(--chip)' }}>
      <div style={{ fontSize: 10, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-display)', color }}>{value}</div>
    </div>
  );
}

function CostoForm({ onSubmit, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ descrizione: '', importo: '', aliquota_iva: 22, data: today, categoria: '' });
  const CATEGORIE = ['Materiali', 'Attrezzatura', 'Smaltimento', 'Manodopera', 'Trasporto', 'Noleggio', 'Altro'];

  const imponibile = parseFloat(form.importo) || 0;
  const aliquota = parseFloat(form.aliquota_iva) || 0;
  const ivaCalc = imponibile * aliquota / 100;
  const totale = imponibile + ivaCalc;

  return (
    <div style={{ padding: 16, border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', marginBottom: 16, background: 'var(--chip)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Descrizione *</label>
          <input placeholder="es. Raccorderia idraulica" value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} style={inputStyle} />
        </div>
        <div><label style={labelStyle}>Importo imponibile (€) *</label><input type="number" step="0.01" placeholder="0.00" value={form.importo} onChange={e => setForm({ ...form, importo: e.target.value })} style={inputStyle} /></div>
        <div>
          <label style={labelStyle}>IVA</label>
          <select value={form.aliquota_iva} onChange={e => setForm({ ...form, aliquota_iva: e.target.value })} style={inputStyle}>
            <option value={0}>Esente</option>
            <option value={4}>4%</option>
            <option value={10}>10%</option>
            <option value={22}>22%</option>
          </select>
        </div>
        <div><label style={labelStyle}>Data</label><input type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} style={inputStyle} /></div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Categoria</label>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {CATEGORIE.map(c => (
              <button key={c} onClick={() => setForm({ ...form, categoria: c.toLowerCase() })}
                style={{ padding: '4px 10px', borderRadius: 6, border: form.categoria === c.toLowerCase() ? '2px solid var(--brand)' : '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 12 }}>
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>
      {imponibile > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 10, fontSize: 12, color: 'var(--ds-muted)' }}>
          <span>IVA: <b style={{ color: 'var(--ink)', fontWeight: 600, fontFamily: 'var(--font-display)' }}>{fmtEur(ivaCalc)}</b></span>
          <span>Totale: <b style={{ color: 'var(--ink)', fontWeight: 600, fontFamily: 'var(--font-display)' }}>{fmtEur(totale)}</b></span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button onClick={onCancel} style={btnCancel}>Annulla</button>
        <button disabled={!form.descrizione || !form.importo} onClick={() => onSubmit(form)}
          style={{ ...btnPrimary, opacity: form.descrizione && form.importo ? 1 : 0.5 }}>Aggiungi</button>
      </div>
    </div>
  );
}

const btnPrimary = { padding: '10px 20px', borderRadius: 6, border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const btnSmall = { padding: '6px 14px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 12 };
const btnCancel = { padding: '8px 16px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-muted)', cursor: 'pointer', fontSize: 12 };
const inputStyle = { padding: '8px 12px', borderRadius: 6, border: '1px solid var(--ds-border)', fontSize: 13, width: '100%', background: 'var(--ds-bg)', color: 'var(--ds-text)' };
const labelStyle = { fontSize: 11, color: 'var(--ds-muted)', display: 'block', marginBottom: 3, textTransform: 'uppercase' };
const actionBtn = { background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 };
